import React, { Fragment, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useBadges } from "./badgesServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/pro-regular-svg-icons";
import { capitalize } from "../util/stringFormat";
import {
  __,
  ascend,
  cond,
  descend,
  equals,
  filter,
  identity,
  ifElse,
  pipe,
  prop,
  propEq,
  sort,
  T,
} from "ramda";
import { useSearchQuery } from "../creator-application/common/use-searchQuery";
import { useMediaTable } from "../creator-application/common/use-mediaTable";

import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";

import BadgeCard from "./BadgeCard";
import EditBadgeDialog from "./EditBadgeDialog";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";

const useStyles = makeStyles(theme => ({
  tableContainer: {
    padding: 0,
    height: "90%",
  },

  icon: {
    color: "black",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const initialState = {
  orderBy: "added",
  orderDirection: "desc",
  page: 0,
};

const sortByAsc = orderBy => sort(ascend(prop(orderBy)));
const sortByDesc = orderBy => sort(descend(prop(orderBy)));

const SortLabel = ({ state, property, handleRequestSort, label }) => {
  const classes = useStyles();
  return (
    <TableSortLabel
      active={state.orderBy === property}
      direction={state.orderDirection}
      onClick={() => handleRequestSort(property)}
    >
      {label}
      {state.orderBy === property ? (
        <span className={classes.visuallyHidden}>
          {state.orderDirection === "desc"
            ? "sorted descending"
            : "sorted ascending"}
        </span>
      ) : null}
    </TableSortLabel>
  );
};

const BadgesTable = ({
  type,
  SelectAllCheckbox,
  BadgeCheckbox,
  showEdit = true,
  showCheckbox = false,
}) => {
  const classes = useStyles();
  const badgesResults = useBadges();

  //-- Manage Query
  const badgesQuery = useSearchQuery(initialState);
  const { handleRequestSort } = useMediaTable(
    { onSortChange: badgesQuery.handleUpdateSort },
    {
      sort: {
        property: badgesQuery.state.orderBy,
        order: badgesQuery.state.orderDirection,
      },
    },
  );

  const byType = type =>
    ifElse(
      () => equals(type, "all"),
      identity,
      filter(propEq("fileType", type)),
    );

  const [badges, setBadges] = useState([]);
  const [badge, setBadge] = useState({});
  useEffect(() => {
    if (badgesResults.isSuccess) {
      const defaultBadges = sortByDesc("added")(badgesResults.data);

      const { orderBy, orderDirection } = badgesQuery.state;
      const isAsc = () => equals(orderDirection, "asc");
      const isDesc = () => equals(orderDirection, "desc");

      const badges = pipe(
        byType(type),
        cond([
          [isAsc, sortByAsc(orderBy)],
          [isDesc, sortByDesc(orderBy)],
          [T, identity],
        ]),
      )(defaultBadges);

      setBadges(badges);
    }
  }, [badgesQuery.state, badgesResults.data, badgesResults.isSuccess, type]);

  //-- Manage Dialog
  const [open, setOpen] = useState(false);
  const handleOpen = badge => event => {
    setBadge(badge);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setBadge({});
  };

  return badgesResults.isLoading ? (
    <PanelLoadingIndicator />
  ) : (
    <Fragment>
      <EditBadgeDialog open={open} initialBadge={badge} onClose={handleClose} />

      <TableContainer classes={{ root: classes.tableContainer }}>
        <Table stickyHeader aria-label="manage badges">
          <TableHead>
            <TableRow>
              {showCheckbox && (
                <TableCell padding="checkbox">
                  <SelectAllCheckbox badges={badges} />
                </TableCell>
              )}

              <TableCell>
                <SortLabel
                  label="Badge"
                  property="fileName"
                  state={badgesQuery.state}
                  handleRequestSort={handleRequestSort}
                />
              </TableCell>

              <TableCell>
                <SortLabel
                  label="Uploaded"
                  property="added"
                  state={badgesQuery.state}
                  handleRequestSort={handleRequestSort}
                />
              </TableCell>

              <TableCell>
                <SortLabel
                  label="Type"
                  property="fileType"
                  state={badgesQuery.state}
                  handleRequestSort={handleRequestSort}
                />
              </TableCell>

              <TableCell>Description</TableCell>

              {showEdit && <TableCell align="center">Edit</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {badges.map(badge => (
              <TableRow key={badge.id}>
                {showCheckbox && (
                  <TableCell>
                    <BadgeCheckbox badge={badge} />
                  </TableCell>
                )}

                <TableCell>
                  <BadgeCard badge={badge} />
                </TableCell>

                <TableCell>
                  {new Date(badge.added).toLocaleDateString()}
                </TableCell>

                <TableCell>{capitalize(badge.fileType)}</TableCell>

                <TableCell>{badge.description}</TableCell>

                {showEdit && (
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton onClick={handleOpen(badge)} size="large">
                        <FontAwesomeIcon
                          className={classes.icon}
                          icon={faPen}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Fragment>
  );
};

export default BadgesTable;
